import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import PortalShell from "@/components/portal/PortalShell";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowRight, Loader2, CheckCircle2, Building2, Handshake } from "lucide-react";
import { COMPANY_TYPES, FINANCE_TYPES, INDUSTRY_SECTORS, COUNTRIES, COMMISSION_TYPES } from "@/pages/hub/constants";
import { MoneyField, CountField, PhoneField, PercentField } from "@/components/ui/number-fields";
import { CompanyLookup } from "@/components/portal/lookup/CompanyLookup";
import { DirectorPick } from "@/components/portal/lookup/DirectorPick";
import { fillEmpty } from "@/lib/lookups";

// Range option sets — human-friendly labels, numeric-friendly values
const DEAL_MIN_OPTIONS = [
  { value: "0",       label: "No minimum" },
  { value: "25000",   label: "£25,000" },
  { value: "50000",   label: "£50,000" },
  { value: "100000",  label: "£100,000" },
  { value: "250000",  label: "£250,000" },
  { value: "500000",  label: "£500,000" },
  { value: "1000000", label: "£1 million" },
  { value: "2000000", label: "£2 million" },
  { value: "5000000", label: "£5 million" },
  { value: "10000000", label: "£10 million" },
  { value: "25000000", label: "£25 million" },
];
const DEAL_MAX_OPTIONS = [
  { value: "100000",     label: "£100,000" },
  { value: "500000",     label: "£500,000" },
  { value: "1000000",    label: "£1 million" },
  { value: "5000000",    label: "£5 million" },
  { value: "10000000",   label: "£10 million" },
  { value: "25000000",   label: "£25 million" },
  { value: "50000000",   label: "£50 million" },
  { value: "100000000",  label: "£100 million" },
  { value: "250000000",  label: "£250 million" },
  { value: "500000000",  label: "£500 million" },
  { value: "1000000000", label: "£1 billion" },
  { value: "0",          label: "No ceiling / unlimited" },
];
const MONTHLY_CAPACITY_OPTIONS = [
  { value: "500000",    label: "Up to £500k / month" },
  { value: "1000000",   label: "£500k – £1m / month" },
  { value: "2500000",   label: "£1m – £2.5m / month" },
  { value: "5000000",   label: "£2.5m – £5m / month" },
  { value: "10000000",  label: "£5m – £10m / month" },
  { value: "25000000",  label: "£10m – £25m / month" },
  { value: "50000000",  label: "£25m – £50m / month" },
  { value: "100000000", label: "£50m – £100m / month" },
  { value: "250000000", label: "£100m – £250m / month" },
  { value: "500000000", label: "£250m – £500m / month" },
  { value: "1000000000", label: "£500m+ / month" },
];
const TURNAROUND_OPTIONS = [
  { value: "1",   label: "24 hours" },
  { value: "3",   label: "2–3 days" },
  { value: "5",   label: "3–5 days" },
  { value: "10",  label: "1–2 weeks" },
  { value: "21",  label: "2–3 weeks" },
  { value: "30",  label: "3–4 weeks" },
  { value: "60",  label: "1–2 months" },
  { value: "90",  label: "2+ months" },
];
const LEADS_PER_MONTH_OPTIONS = [
  { value: "1-5",     label: "Just getting started · 1–5 leads / month" },
  { value: "5-10",    label: "5 – 10 leads / month" },
  { value: "10-25",   label: "10 – 25 leads / month" },
  { value: "25-50",   label: "25 – 50 leads / month" },
  { value: "50-100",  label: "50 – 100 leads / month" },
  { value: "100+",    label: "100+ leads / month" },
];
const YEARS_ACTIVE_OPTIONS = [
  { value: "0",   label: "Less than 1 year" },
  { value: "1",   label: "1 – 2 years" },
  { value: "3",   label: "3 – 5 years" },
  { value: "6",   label: "6 – 10 years" },
  { value: "11",  label: "11 – 20 years" },
  { value: "21",  label: "20+ years" },
];

const chipCls = (active) =>
  `inline-flex items-center rounded-full px-2.5 h-7 text-[11.5px] font-medium border transition-colors cursor-pointer ${
    active
      ? "bg-[#0055FF] border-[#0055FF] text-white"
      : "bg-white/[0.04] border-white/15 text-slate-200 hover:border-white/40"
  }`;

const fieldLabelCls = "text-[10.5px] uppercase tracking-[0.14em] text-slate-400 font-semibold";
const fieldInputCls = "mt-1.5 bg-white/[0.04] border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-[#0055FF]/40";

export default function PortalApply({ kind }) {
  const isLender = kind === "lender";
  const nav = useNavigate();
  const [params] = useSearchParams();
  const refId = params.get("ref") || "";
  const [refName, setRefName] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  /* A partner's own link should say whose it is. If the id no longer resolves we say it plainly
     rather than naming the wrong firm. */
  useEffect(() => {
    if (!refId) return;
    api.get(`/apply/partner-ref/${refId}`)
      .then(({ data }) => setRefName(data?.partner?.name || ""))
      .catch(() => setRefName(""));
  }, [refId]);

  const cfg = isLender ? {
    eyebrow: "Become a Connexis lending partner",
    title:   "Apply to receive curated deal flow.",
    subtitle:"Tell us what you fund. If it's a fit we'll invite you to the lender desk within 48 hours.",
    endpoint:"/hub/apply/lender",
    testId:  "lender-apply",
  } : {
    eyebrow: "Become a Connexis introducer",
    title:   "Apply to submit applications & earn on funded deals.",
    subtitle:"Tell us about your book. We work with brokers, IFAs, accountants, and business networks.",
    endpoint:"/hub/apply/introducer",
    testId:  "introducer-apply",
  };

  const [form, setForm] = useState(isLender ? {
    company_name: "", contact_name: "", contact_title: "", email: "", phone: "",
    website: "", company_number: "", address: "",
    company_type: "Lender", finance_types: [], industry_sectors: [], countries_covered: [],
    min_deal_size_gbp: "", max_deal_size_gbp: "", turnaround_days: "",
    monthly_capacity_gbp: "", expected_fee_pct: "",
    lending_criteria: "", notes: "",
  } : {
    name: "", email: "", phone: "", company: "", company_number: "", website: "", address: "",
    fca_regulated: false, fca_number: "", years_active: "", monthly_volume_estimate: "",
    finance_types: [], industry_sectors: [], countries_covered: [],
    preferred_commission_type: "revenue_share",
    notes: "",
  });

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [directors, setDirectors] = useState([]);
  const toggleIn = (key, val) => setForm((f) => ({
    ...f,
    [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val],
  }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      // Cast numeric fields
      for (const k of ["min_deal_size_gbp", "max_deal_size_gbp", "turnaround_days", "monthly_capacity_gbp", "expected_fee_pct", "years_active"]) {
        if (payload[k] !== undefined && payload[k] !== "") payload[k] = parseFloat(payload[k]) || 0;
        else if (payload[k] === "") delete payload[k];
      }
      if (refId) payload.referred_by = refId;
      await api.post(cfg.endpoint, payload);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <PortalShell kind={kind} variant="apply" eyebrow={cfg.eyebrow}>
        <div className="max-w-lg mx-auto rounded-3xl border border-emerald-400/30 bg-emerald-400/[0.06] p-10 text-center" data-testid={`${cfg.testId}-success`}>
          <div className="mx-auto grid place-items-center size-12 rounded-full bg-emerald-400/20 text-emerald-300">
            <CheckCircle2 className="size-6" />
          </div>
          <h2 className="mt-4 text-[24px] font-semibold text-white tracking-tight">Application received</h2>
          <p className="mt-2 text-[14px] text-slate-300 leading-relaxed">
            Thanks {isLender ? "" : "for applying"} — we&apos;ll review your details and be in touch within 48 hours. If approved, you&apos;ll receive an invite to activate your portal.
          </p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <Button asChild variant="outline" className="border-white/15 bg-white/[0.04] text-white hover:bg-white/[0.08]">
              <Link to={isLender ? "/lender/login" : "/introducer/login"}>Back to sign in</Link>
            </Button>
            <Button asChild className="bg-white text-[#0B1220] hover:bg-white/90">
              <Link to="/">Explore Connexis</Link>
            </Button>
          </div>
        </div>
      </PortalShell>
    );
  }

  return (
    <PortalShell kind={kind} variant="apply" eyebrow={cfg.eyebrow} title={cfg.title} subtitle={cfg.subtitle}>
      <form onSubmit={submit} className="rounded-3xl border border-white/[0.08] bg-white/[0.03] backdrop-blur-sm p-6 sm:p-8 max-w-4xl" data-testid={cfg.testId}>
        {refId && (
          <div className="mb-6 flex max-w-md items-start gap-2.5 rounded-2xl border border-emerald-400/25 bg-emerald-400/[0.07] px-3.5 py-2.5 text-[12.5px] leading-relaxed text-emerald-300" data-testid="apply-referral-chip">
            <Handshake className="mt-0.5 size-4 shrink-0" />
            <span>
              Introduced by <strong className="font-semibold text-emerald-200">{refName || "a Connexis partner"}</strong>
              {" "}— their introduction is attached to this application.
            </span>
          </div>
        )}
        {/* SECTION 1 — About you */}
        <SectionHead icon={isLender ? Building2 : Handshake} label={isLender ? "About your firm" : "About you"} />
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          {isLender ? <>
            <CompanyLookup dark label="Find your firm" testId="apply-firm-lookup" onPick={(c) => {
              const vals = { company_name: c.business_name, company_number: c.company_number, address: c.company_address };
              setForm((f) => fillEmpty(f, vals).next);
              setDirectors(c.directors || []);
              return fillEmpty(form, vals).filled;
            }} />
            <DirectorPick dark directors={directors} value={form.contact_name} testId="apply-lender-director" className="col-span-full"
                          note="The firm's directors, from Connexis search. Someone else? Just type your name below."
                          onPick={(name, d) => setForm((f) => ({ ...f, contact_name: name, contact_title: f.contact_title || d.role || "" }))} />
            <Field label="Company name *"><Input required value={form.company_name} onChange={(e) => setField("company_name", e.target.value)} className={fieldInputCls} data-testid="apply-company-name" /></Field>
            <Field label="Contact name *"><Input required value={form.contact_name} onChange={(e) => setField("contact_name", e.target.value)} className={fieldInputCls} data-testid="apply-contact-name" /></Field>
            <Field label="Contact title"><Input value={form.contact_title} onChange={(e) => setField("contact_title", e.target.value)} placeholder="Head of Origination" className={fieldInputCls} /></Field>
            <Field label="Email *"><Input required type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} className={fieldInputCls} data-testid="apply-email" /></Field>
            <Field label="Mobile (for SMS deal alerts)"><PhoneField value={form.phone} onChange={(v) => setField("phone", v)} dark testId="apply-lender-phone" /></Field>
            <Field label="Website"><Input value={form.website} onChange={(e) => setField("website", e.target.value)} placeholder="https://…" className={fieldInputCls} /></Field>
            <Field label="Companies House number"><Input value={form.company_number} onChange={(e) => setField("company_number", e.target.value)} className={fieldInputCls} data-testid="apply-lender-company-number" /></Field>
            <Field label="Firm type *">
              <Select value={form.company_type} onValueChange={(v) => setField("company_type", v)}>
                <SelectTrigger className={fieldInputCls} data-testid="apply-firm-type"><SelectValue /></SelectTrigger>
                <SelectContent>{COMPANY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Registered address" fullWidth><Input value={form.address} onChange={(e) => setField("address", e.target.value)} className={fieldInputCls} data-testid="apply-lender-address" /></Field>
          </> : <>
            <CompanyLookup dark label="Find your company" testId="apply-intro-company-lookup" onPick={(c) => {
              const vals = { company: c.business_name, company_number: c.company_number, address: c.company_address };
              setForm((f) => fillEmpty(f, vals).next);
              setDirectors(c.directors || []);
              return fillEmpty(form, vals).filled;
            }} />
            <DirectorPick dark directors={directors} value={form.name} testId="apply-intro-director" className="col-span-full"
                          onPick={(name) => setForm((f) => ({ ...f, name }))} />
            <Field label="Full name *"><Input required value={form.name} onChange={(e) => setField("name", e.target.value)} className={fieldInputCls} data-testid="apply-name" /></Field>
            <Field label="Email *"><Input required type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} className={fieldInputCls} data-testid="apply-email" /></Field>
            <Field label="Mobile (for SMS deal alerts)"><PhoneField value={form.phone} onChange={(v) => setField("phone", v)} dark testId="apply-introducer-phone" /></Field>
            <Field label="Company"><Input value={form.company} onChange={(e) => setField("company", e.target.value)} className={fieldInputCls} data-testid="apply-company" /></Field>
            <Field label="Website"><Input value={form.website} onChange={(e) => setField("website", e.target.value)} placeholder="https://…" className={fieldInputCls} /></Field>
            <Field label="Companies House number"><Input value={form.company_number} onChange={(e) => setField("company_number", e.target.value)} className={fieldInputCls} data-testid="apply-intro-company-number" /></Field>
            <Field label="Address" fullWidth><Input value={form.address} onChange={(e) => setField("address", e.target.value)} className={fieldInputCls} data-testid="apply-intro-address" /></Field>
            <Field label="FCA regulated?">
              <label className="mt-2 inline-flex items-center gap-2 text-[13px] text-slate-200">
                <Checkbox checked={form.fca_regulated} onCheckedChange={(v) => setField("fca_regulated", !!v)} data-testid="apply-fca-regulated" />
                Yes — I am FCA regulated
              </label>
            </Field>
            {form.fca_regulated && <Field label="FCA number"><Input value={form.fca_number} onChange={(e) => setField("fca_number", e.target.value)} className={fieldInputCls} /></Field>}
            <Field label="Years active in finance">
              <Select value={form.years_active !== "" ? String(form.years_active) : undefined} onValueChange={(v) => setField("years_active", v)}>
                <SelectTrigger className={fieldInputCls}><SelectValue placeholder="Pick…" /></SelectTrigger>
                <SelectContent>{YEARS_ACTIVE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Leads you introduce per month">
              <Select value={form.monthly_volume_estimate || undefined} onValueChange={(v) => setField("monthly_volume_estimate", v)}>
                <SelectTrigger className={fieldInputCls} data-testid="apply-monthly-leads"><SelectValue placeholder="Pick a range…" /></SelectTrigger>
                <SelectContent>{LEADS_PER_MONTH_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </>}
        </div>

        {/* SECTION 2 — Appetite / preferences */}
        <SectionHead label={isLender ? "Lending appetite" : "Types of finance you introduce"} className="mt-8" />
        <p className="mt-1 text-[12.5px] text-slate-400">Pick as many as apply.</p>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {FINANCE_TYPES.map((t) => (
            <button type="button" key={t} onClick={() => toggleIn("finance_types", t)} className={chipCls(form.finance_types.includes(t))} data-testid={`apply-ft-${t}`}>{t}</button>
          ))}
        </div>

        <div className="mt-6">
          <div className={fieldLabelCls}>{isLender ? "Sectors you cover" : "Sectors you tend to introduce from"}</div>
          <div className="mt-2.5 flex flex-wrap gap-1.5 max-h-40 overflow-y-auto pr-1">
            {INDUSTRY_SECTORS.map((s) => (
              <button type="button" key={s} onClick={() => toggleIn("industry_sectors", s)} className={chipCls(form.industry_sectors.includes(s))}>{s}</button>
            ))}
          </div>
        </div>

        <div className="mt-6">
          <div className={fieldLabelCls}>{isLender ? "Countries you fund in" : "Countries you cover"}</div>
          <p className="mt-1 text-[12.5px] text-slate-400">
            {isLender ? "Where can you lend?" : "Which countries do your clients come from?"} Pick as many as apply.
          </p>
          <div className="mt-2.5 flex flex-wrap gap-1.5 max-h-40 overflow-y-auto pr-1" data-testid="apply-countries">
            {COUNTRIES.map((c) => (
              <button type="button" key={c} onClick={() => toggleIn("countries_covered", c)}
                      className={chipCls(form.countries_covered.includes(c))}
                      data-testid={`apply-country-${c}`}>{c}</button>
            ))}
          </div>
        </div>

        {isLender ? (
          <div className="grid sm:grid-cols-3 gap-4 mt-6">
            <Field label="Min deal size">
              <MoneyField value={form.min_deal_size_gbp} onChange={(v) => setField("min_deal_size_gbp", v)} dark compact testId="apply-min-deal" hint="drag or type" />
            </Field>
            <Field label="Max deal size">
              <MoneyField value={form.max_deal_size_gbp} onChange={(v) => setField("max_deal_size_gbp", v)} dark compact testId="apply-max-deal" hint="0 = unlimited" />
            </Field>
            <Field label="Typical turnaround">
              <div className="mt-1"><CountField value={form.turnaround_days} onChange={(v) => setField("turnaround_days", v)} max={90} suffix="working days" dark testId="apply-turnaround" /></div>
            </Field>
            <Field label="Monthly deployable capacity" fullWidth>
              <MoneyField value={form.monthly_capacity_gbp} onChange={(v) => setField("monthly_capacity_gbp", v)} ladder="capacity" dark compact testId="apply-monthly-capacity" hint="how much you can deploy each month" />
            </Field>
            <Field label="Your typical fee on a funded deal" fullWidth>
              <PercentField value={form.expected_fee_pct} onChange={(v) => setField("expected_fee_pct", v)} max={20} step={0.25} presets={[1, 1.5, 2, 2.5, 3, 5, 10, 20]} dark testId="apply-expected-fee"
                            hint="as a % of the facility — this is what you earn, not what you pay us. It sizes every case before it funds; the real figure is confirmed per deal and you can change it in your portal at any time." />
            </Field>
            <Field label="Lending criteria (free-text)" fullWidth>
              <Textarea value={form.lending_criteria} onChange={(e) => setField("lending_criteria", e.target.value)} className={`min-h-[120px] ${fieldInputCls}`} placeholder="e.g. Property secured with min 30% deposit. UK-only. Sectors we avoid: leisure, waste." data-testid="apply-lending-criteria" />
            </Field>
          </div>
        ) : (
          <div className="mt-6">
            <div className={fieldLabelCls}>Preferred commission model</div>
            <div className="mt-2 grid sm:grid-cols-3 gap-2">
              {COMMISSION_TYPES.map((c) => (
                <button type="button" key={c.key} onClick={() => setField("preferred_commission_type", c.key)}
                        className={`text-left rounded-2xl border p-3 transition-colors ${form.preferred_commission_type === c.key ? "bg-[#0055FF]/[0.15] border-[#0055FF]/60" : "bg-white/[0.03] border-white/10 hover:border-white/25"}`}
                        data-testid={`apply-comm-${c.key}`}>
                  <div className="text-[13px] font-semibold text-white">{c.label}</div>
                  <div className="mt-1 text-[11px] text-slate-400 leading-snug">{c.hint}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        <Field label="Anything else we should know?" className="mt-6" fullWidth>
          <Textarea value={form.notes} onChange={(e) => setField("notes", e.target.value)} className={`min-h-[80px] ${fieldInputCls}`} placeholder="Optional…" />
        </Field>

        <div className="mt-8 flex items-center justify-between flex-wrap gap-3">
          <div className="text-[11.5px] text-slate-500 max-w-md">
            By submitting you agree to Connexis reviewing your details for eligibility. We&apos;ll never share your firm data with third parties.
          </div>
          <Button type="submit" disabled={busy} className="h-11 bg-[#0055FF] hover:bg-[#0044CC] rounded-xl font-semibold px-6" data-testid={`${cfg.testId}-submit`}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <>Submit application <ArrowRight className="size-4 ml-1" /></>}
          </Button>
        </div>
      </form>
    </PortalShell>
  );
}

function SectionHead({ icon: Icon, label, className = "" }) {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {Icon && <Icon className="size-4 text-[#7da9ff]" />}
      <div className="text-[10.5px] uppercase tracking-[0.24em] font-semibold text-[#7da9ff]">{label}</div>
    </div>
  );
}

function Field({ label, children, fullWidth = false, className = "" }) {
  return (
    <div className={`${fullWidth ? "col-span-full" : ""} ${className}`}>
      <Label className={fieldLabelCls}>{label}</Label>
      <div>{children}</div>
    </div>
  );
}
